import React, { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { Logo } from "./Logo";

const LANGS = ["az", "en", "ru"];

const Navbar = () => {
  const { lang, setLang, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { id: "about", label: t.nav.about },
    { id: "services", label: t.nav.services },
    { id: "projects", label: t.nav.projects },
    { id: "process", label: t.nav.process },
    { id: "why", label: t.nav.why },
    { id: "contact", label: t.nav.contact },
  ];

  const scrollTo = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-[#030407]/80 border-b border-white/5 backdrop-blur-xl" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
        <button
          data-testid="nav-logo-link"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-center"
          aria-label="BE1 home"
        >
          <Logo size="sm" />
        </button>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-8" aria-label="Primary">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => scrollTo(l.id)}
              className="font-body text-sm tracking-wide text-[#ffff] hover:text-white transition-colors relative group"
            >
              {l.label}
              <span className="absolute left-0 -bottom-1 h-px w-0 bg-gradient-to-r from-[#00F0FF] to-[#0055FF] transition-all duration-300 group-hover:w-full" />
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          {/* Language switcher */}
          <div
            data-testid="language-switcher"
            className="hidden sm:flex items-center gap-1 rounded-full border border-white/10 bg-white/[0.03] backdrop-blur p-1"
          >
            {LANGS.map((code) => (
              <button
                key={code}
                data-testid={`lang-btn-${code}`}
                onClick={() => setLang(code)}
                className={`px-3 py-1 text-[11px] tracking-[0.18em] uppercase font-semibold rounded-full transition-all ${
                  lang === code
                    ? "bg-gradient-to-r from-[#00F0FF] to-[#0055FF] text-[#001016] shadow-[0_0_20px_rgba(0,240,255,0.4)]"
                    : "text-[#9BA1B0] hover:text-white"
                }`}
              >
                {code}
              </button>
            ))}
          </div>

          <button
            data-testid="nav-cta-button"
            onClick={() => scrollTo("contact")}
            className="hidden md:inline-flex magnetic-btn btn-primary text-sm py-2.5 px-5"
          >
            {t.nav.cta}
          </button>

          {/* Mobile menu */}
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden p-2 rounded-lg border border-white/10 bg-white/[0.03]"
            aria-label="Toggle menu"
            aria-expanded={open}
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden border-t border-white/5 bg-[#030407]/95 backdrop-blur-xl" data-testid="mobile-menu">
          <nav className="px-6 py-6 flex flex-col gap-4" aria-label="Mobile">
            {links.map((l) => (
              <button
                key={l.id}
                data-testid={`mobile-nav-link-${l.id}`}
                onClick={() => scrollTo(l.id)}
                className="text-left text-[#cfd2da] hover:text-white font-body text-base"
              >
                {l.label}
              </button>
            ))}
            <div className="flex items-center gap-2 mt-2">
              {LANGS.map((code) => (
                <button
                  key={code}
                  data-testid={`mobile-lang-btn-${code}`}
                  onClick={() => setLang(code)}
                  className={`px-3 py-1.5 text-[11px] tracking-[0.18em] uppercase font-semibold rounded-full transition-all ${
                    lang === code
                      ? "bg-gradient-to-r from-[#00F0FF] to-[#0055FF] text-[#001016]"
                      : "border border-white/10 text-[#9BA1B0]"
                  }`}
                >
                  {code}
                </button>
              ))}
            </div>
            <button
              data-testid="mobile-cta-button"
              onClick={() => scrollTo("contact")}
              className="magnetic-btn btn-primary text-sm py-3 mt-2"
            >
              {t.nav.cta}
            </button>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Navbar;
